/**
 * @file index.tsx
 *
 * @fileoverview Shows organization benefits when in a view mode.
 */
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { OrganizationBenefitsWidgetEmpty } from '~/jobiqo/components/Organization/Widgets/Benefits/Empty';
import { OrganizationWidgetsBoxLabel } from '~/jobiqo/components/Organization/Widgets/BoxLabel';
import { EditLink } from '~/jobiqo/components/Organization/Widgets/EditLink';
import { OrganizationProfile } from '~/jobiqo/types/types';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { FlexItem } from '~/vcl/atoms/06-layout/03-flex/02-flex-item';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Box } from '~/vcl/atoms/07-box';
import { Text } from '~/vcl/atoms/13-text/03-text';
import {
  Expander,
  ExpanderContent,
  ExpanderTrigger,
} from '~/vcl/atoms/22-expander';
import { CheckIcon } from '~/vcl/icons/Check';

export interface OrganizationBenefitsWidgetViewProps {
  /**
   * The current organization profile.
   */
  organization: OrganizationProfile;
  /**
   * The current box id.
   */
  boxId: string | undefined;
}

export const OrganizationBenefitsWidgetView = ({
  organization,
  boxId,
}: OrganizationBenefitsWidgetViewProps) => {
  const intl = useIntl();
  const [showMoreBenefits, setShowMoreBenefits] = useState(false);

  if (!organization?.benefits?.length) {
    return (
      <OrganizationBenefitsWidgetEmpty
        organization={organization}
        boxId={boxId}
      />
    );
  }

  const benefitsList = (
    <>
      {organization?.benefits?.map((benefit, index) => {
        return (
          <FlexItems flexWrap="nowrap" alignItems="center" key={index}>
            <CheckIcon
              width="18"
              height="18"
              color="gray7"
              style={{ flexShrink: 0 }}
            />
            <FlexItem marginLeft={3}>
              <Text marginTop={2} color="gray4">
                {benefit?.label}
              </Text>
            </FlexItem>
          </FlexItems>
        );
      })}
    </>
  );

  return (
    <Box
      label={
        <OrganizationWidgetsBoxLabel>
          {intl.formatMessage({
            id: 'organization_benefits_label',
            defaultMessage: 'Organization benefits',
          })}
        </OrganizationWidgetsBoxLabel>
      }
    >
      <FlexItems justifyContent="flex-end">
        <EditLink
          boxId={boxId}
          organization={organization}
          boxHasTitle={true}
        />
      </FlexItems>
      <FlexItems flexDirection="column">
        {(organization?.benefits?.length || 0) > 4 ? (
          <Expander
            isOpen={showMoreBenefits}
            onChange={() => setShowMoreBenefits(!showMoreBenefits)}
          >
            <ExpanderContent closedMinHeight="100px">
              {benefitsList}
            </ExpanderContent>
            <Spacing />
            <ExpanderTrigger>
              <Text tag="span" color="primary">
                {showMoreBenefits ? (
                  <FormattedMessage
                    id="organization_benefits_show_less"
                    defaultMessage="Show less"
                  />
                ) : (
                  <FormattedMessage
                    id="organization_benefits_show_more"
                    defaultMessage="Show more"
                  />
                )}
              </Text>
            </ExpanderTrigger>
          </Expander>
        ) : (
          benefitsList
        )}
      </FlexItems>
    </Box>
  );
};
